import { compact, omit } from "lodash";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import {
  AssessmentGroupProviders,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  ComplianceProgramRecurrence,
  CreateProgramScheduledReminderMutationMutationVariables,
  ProgramScheduledReminderInput,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button, { buttonTheme } from "@components/button/button";
import { TableBodyCell } from "@components/table/table";
import { TableBodyRow } from "@components/table/table";
import { TableBody } from "@components/table/table";
import { Table, TableHeadRow } from "@components/table/table";
import { TableHeadCell } from "@components/table/table";
import { TableContainer } from "@components/table/table";
import { Tooltip } from "@components/tooltip/tooltip";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { getAssessmentTypeLabel } from "@helpers/constants";
import useConfirm from "@helpers/hooks/use-confirm";

import ComplianceProgramReminderDialog from "../compliance-program-reminder-dialog";
import { ComplianceProgramStep, ProgramForm } from "./types";

const ComplianceProgramFormReminders = ({
  proposedComplianceProgram,
  onUpdateProposedComplianceProgram,
  canUpdate,
  complianceProgramSteps,
}: {
  proposedComplianceProgram: ProgramForm;
  onUpdateProposedComplianceProgram: (data: ProgramForm) => void;
  canUpdate: boolean;
  complianceProgramSteps: ComplianceProgramStep[];
}) => {
  const label = useLabel();
  const [isShowingSendReminderDialog, setIsShowingSendReminderDialog] =
    useState(false);

  const scheduledReminders = useMemo(
    () =>
      (proposedComplianceProgram.scheduledReminders ||
        []) as ProgramScheduledReminderInput[],
    [proposedComplianceProgram.scheduledReminders]
  );

  const handleClickCreateReminder = useCallback(
    (
      variables: Omit<
        CreateProgramScheduledReminderMutationMutationVariables,
        "complianceProgramId"
      >
    ) => {
      onUpdateProposedComplianceProgram({
        ...proposedComplianceProgram,
        scheduledReminders: [
          ...scheduledReminders,
          omit(variables, "sendNow", "complianceProgramId"),
        ],
      });
      setIsShowingSendReminderDialog(false);
    },
    [
      onUpdateProposedComplianceProgram,
      proposedComplianceProgram,
      scheduledReminders,
    ]
  );

  const { confirm, ConfirmationDialog } = useConfirm(
    "Delete reminder?",
    "Are you sure you want to delete this reminder?"
  );

  const handleDeleteReminder = useCallback(
    async (indexToRemove: number, idToRemove?: number | null) => {
      const confirmed = await confirm();
      if (confirmed) {
        onUpdateProposedComplianceProgram({
          ...proposedComplianceProgram,
          scheduledReminders: scheduledReminders.filter((reminder, index) => {
            if (idToRemove) {
              return reminder.id !== idToRemove;
            }
            return index !== indexToRemove;
          }),
        });
      }
    },
    [
      onUpdateProposedComplianceProgram,
      proposedComplianceProgram,
      scheduledReminders,
      confirm,
    ]
  );

  const assessmentTemplates = (proposedComplianceProgram.assessmentTemplates ??
    []) as AssessmentTemplateComplianceRequirement[];
  const peerAssessmentTemplate = assessmentTemplates.find(
    (template) => template.assessmentType === AssessmentType.Peer
  );

  const cannotCreateReminder = useMemo(() => {
    if (
      !proposedComplianceProgram.startDate &&
      !proposedComplianceProgram.ongoing
    ) {
      return "Program must have a start date";
    }
    if (complianceProgramSteps.length === 0) {
      return "Program must have at least one step";
    }
    return null;
  }, [
    complianceProgramSteps,
    proposedComplianceProgram.startDate,
    proposedComplianceProgram.ongoing,
  ]);

  const programRecurrence =
    proposedComplianceProgram.recurrence ?? ComplianceProgramRecurrence.None;
  const programOngoing = proposedComplianceProgram.ongoing ?? false;
  const showRelativeDateInput =
    programRecurrence !== ComplianceProgramRecurrence.None || programOngoing;

  return (
    <>
      <ConfirmationDialog />
      {isShowingSendReminderDialog && (
        <ComplianceProgramReminderDialog
          complianceProgramStartDate={moment(
            proposedComplianceProgram.startDate
          ).toDate()}
          complianceProgramRecurrence={programRecurrence}
          showRelativeDateInput={showRelativeDateInput} // always show in dialog
          complianceProgramOngoing={programOngoing}
          onClose={() => setIsShowingSendReminderDialog(false)}
          cols={compact([
            complianceProgramSteps.includes(
              ComplianceProgramStep.SELF_MANAGER_REVIEW
            ) && {
              label: `${getAssessmentTypeLabel(
                AssessmentType.Performance,
                true
              )} ${label("review", { titleCase: true })}`,
              keyName: "performanceSelfAssessmentStatus",
            },
            complianceProgramSteps.includes(
              ComplianceProgramStep.SELF_MANAGER_REVIEW
            ) && {
              label: `${getAssessmentTypeLabel(
                AssessmentType.Performance
              )} ${label("review", { titleCase: true })}`,
              keyName: "performanceAssessmentStatus",
            },
            complianceProgramSteps.includes(
              ComplianceProgramStep.UPWARD_REVIEW
            ) && {
              label: `${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
                "review",
                { titleCase: true }
              )}`,
              keyName: "managerAssessmentStatus",
            },
            complianceProgramSteps.includes(
              ComplianceProgramStep.PEER_REVIEW
            ) &&
              peerAssessmentTemplate &&
              peerAssessmentTemplate?.providers !==
                AssessmentGroupProviders.Default && {
                label: `${getAssessmentTypeLabel(
                  AssessmentType.Peer
                )} Nominations`,
                keyName: "nominations",
              },
            complianceProgramSteps.includes(
              ComplianceProgramStep.PEER_REVIEW
            ) && {
              label: `${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                { titleCase: true }
              )}`,
              keyName: "peerAssessmentStatus",
            },
            complianceProgramSteps.includes(
              ComplianceProgramStep.ONE_ON_ONE
            ) && {
              label: `${label("oneonone", { titleCase: true })} Meetings`,
              keyName: "oneononeStatus",
            },
            complianceProgramSteps.includes(
              ComplianceProgramStep.ENGAGEMENT_SURVEY
            ) && {
              label: `${getAssessmentTypeLabel(
                AssessmentType.EngagementSurvey
              )}`,
              keyName: "engagementSurveyStatus",
            },
          ])}
          selectedTeam={null}
          onSave={handleClickCreateReminder}
          createProgramReminderLoading={false}
          disallowSendNow
        />
      )}
      <div className="mt-8 flex items-center justify-between">
        <div className="text-xl font-semibold">Scheduled Reminders</div>
        {canUpdate && (
          <Button
            text="Schedule a Reminder"
            tooltip={cannotCreateReminder}
            disabled={!!cannotCreateReminder}
            theme={buttonTheme.lightBlue}
            onClick={() => setIsShowingSendReminderDialog(true)}
          />
        )}
      </div>

      <div className="mt-6">
        <TableContainer className="mt-6">
          <Table>
            <TableHeadRow>
              <TableHeadCell roundedLeft widthPx={220}>
                Scheduled Date
              </TableHeadCell>
              <TableHeadCell widthPx={192}>
                {label("review", { titleCase: true })} Steps
              </TableHeadCell>
              <TableHeadCell widthPx={192}>Reminder Type</TableHeadCell>
              <TableHeadCell>Message</TableHeadCell>
              <TableHeadCell widthPx={24}> </TableHeadCell>
            </TableHeadRow>
            <TableBody>
              {scheduledReminders.map((reminder, index) => (
                <TableBodyRow key={`reminder-${reminder.id ?? index}`}>
                  <TableBodyCell className="text-gray-800 w-72">
                    {showRelativeDateInput
                      ? `${reminder.daysAfterStartDate} days after start date`
                      : `${moment(proposedComplianceProgram.startDate)
                          .add(reminder.daysAfterStartDate, "days")
                          .format("MMM D, YYYY")}`}
                  </TableBodyCell>
                  <TableBodyCell>
                    {compact([
                      reminder.nominations && "Peer Nominations",
                      reminder.oneonone &&
                        label("oneonone", { titleCase: true }),
                      reminder.performanceAssessment &&
                        `${getAssessmentTypeLabel(
                          AssessmentType.Performance
                        )} ${label("review", { titleCase: true })}`,
                      reminder.performanceSelfAssessment &&
                        `${getAssessmentTypeLabel(
                          AssessmentType.Performance,
                          true
                        )} ${label("review", { titleCase: true })}`,
                      reminder.managerAssessment &&
                        getAssessmentTypeLabel(AssessmentType.Manager),
                      reminder.peerAssessment &&
                        getAssessmentTypeLabel(AssessmentType.Peer),
                      reminder.engagementSurvey &&
                        getAssessmentTypeLabel(AssessmentType.EngagementSurvey),
                    ]).join(", ")}
                  </TableBodyCell>
                  <TableBodyCell>
                    {compact([
                      reminder.emailNotification && "Email",
                      reminder.slackNotification && "Slack",
                    ]).join(", ")}
                  </TableBodyCell>
                  <TableBodyCell>
                    <Tooltip text={reminder.message}>
                      <span className="truncate inline-block max-w-48">
                        {reminder.message || "N/A"}
                      </span>
                    </Tooltip>
                  </TableBodyCell>
                  <TableBodyCell>
                    {canUpdate && (
                      <Button
                        icon={getTopicflowIcon("trash")}
                        theme={buttonTheme.iconGray}
                        onClick={() => handleDeleteReminder(index, reminder.id)}
                      />
                    )}
                  </TableBodyCell>
                </TableBodyRow>
              ))}
              {scheduledReminders.length === 0 && (
                <TableBodyRow>
                  <TableBodyCell
                    colSpan={5}
                    className="text-center p-4 text-gray-500"
                  >
                    No reminders.
                  </TableBodyCell>
                </TableBodyRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default ComplianceProgramFormReminders;
